import { TRANSLATION_MODAL_MESSAGES } from '@constants';
import { stripHtml } from '@utilities';

import { getTranslationModalMessage } from './getTranslationModalMessage';

export const FORMATTING_MISMATCH_WARNING = TRANSLATION_MODAL_MESSAGES.formattingMismatch;

// Accept both semantic and legacy HTML tags for supported marks.
const FORMAT_MARKS = [
  { name: 'bold', pattern: /<(?:strong|b)\b/i },
  { name: 'italic', pattern: /<(?:em|i)\b/i },
  { name: 'underline', pattern: /<u\b/i },
];

const hasText = (richText) => Boolean(stripHtml(richText || ''));

const getVariableTokens = (richText) => {
  if (typeof document === 'undefined') return [];
  const container = document.createElement('div');
  container.innerHTML = richText || '';
  const tokens = Array.from(container.querySelectorAll('[data-mention-id]'))
    .map((node) => (node.textContent || '').trim())
    .filter(Boolean);
  return Array.from(new Set(tokens));
};

// A variable counts as present whether it remains a mention span or is typed literally.
const getMissingVariables = (sourceRichText, translationRichText) => {
  const sourceTokens = getVariableTokens(sourceRichText);
  if (!sourceTokens.length) return [];
  const translationText = stripHtml(translationRichText || '');
  return sourceTokens.filter((token) => !translationText.includes(token));
};

const getFormatMarks = (richText) =>
  new Set(
    FORMAT_MARKS.filter(({ pattern }) => pattern.test(richText || '')).map(({ name }) => name)
  );

const hasFormattingMismatch = (sourceRichText, translationRichText) => {
  const sourceMarks = getFormatMarks(sourceRichText);
  const translationMarks = getFormatMarks(translationRichText);
  if (sourceMarks.size !== translationMarks.size) return true;
  return [...sourceMarks].some((mark) => !translationMarks.has(mark));
};

/**
 * Warnings for translated text that drops source variables or formatting.
 * @param {Object} params
 * @param {String} [params.language] survey source-language code (e.g. 'fr'); English when omitted
 * @param {String} params.sourceRichText source (original-language) rich text HTML
 * @param {String} params.translationRichText current translated rich text HTML
 * @return {String[]} ordered warning messages (missing variables first, then formatting)
 */
export const getTranslationWarnings = ({ language, sourceRichText, translationRichText }) => {
  if (!hasText(translationRichText)) return [];
  const missingVariableMessage = getTranslationModalMessage('missingVariable', language);
  const warnings = getMissingVariables(sourceRichText, translationRichText).map((token) =>
    missingVariableMessage.replace('{variable}', token)
  );
  if (hasFormattingMismatch(sourceRichText, translationRichText)) {
    warnings.push(getTranslationModalMessage('formattingMismatch', language));
  }
  return warnings;
};
